import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface QuickLogWidgetProps {
  config: {
    title?: string;
    logType?: string;
    buttonText?: string;
    successMessage?: string;
    colorScheme?: 'primary' | 'success' | 'warning' | 'destructive';
  };
  data?: {
    todayCount?: number;
    lastLogTime?: string;
  };
  onLog?: () => Promise<void>;
}

export function QuickLogWidget({ config, data, onLog }: QuickLogWidgetProps) {
  const [isLogging, setIsLogging] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const title = config.title || 'Quick Log';
  const buttonText = config.buttonText || 'Log Now';
  const todayCount = data?.todayCount ?? 0;

  const handleLog = async () => {
    if (!onLog) return;
    
    setIsLogging(true);
    try {
      await onLog();
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error('Log failed:', error);
    } finally {
      setIsLogging(false);
    }
  };

  const colorClasses = {
    primary: 'bg-primary hover:bg-primary/90',
    success: 'bg-green-600 hover:bg-green-700',
    warning: 'bg-yellow-600 hover:bg-yellow-700',
    destructive: 'bg-destructive hover:bg-destructive/90',
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center justify-center gap-4">
        <Button
          onClick={handleLog}
          disabled={isLogging || showSuccess || !onLog}
          className={cn(
            "w-16 h-16 rounded-full transition-all",
            colorClasses[config.colorScheme || 'primary'],
            showSuccess && "bg-green-600 hover:bg-green-600"
          )}
        >
          {isLogging ? (
            <Loader2 className="h-8 w-8 animate-spin" />
          ) : showSuccess ? (
            <Check className="h-8 w-8" />
          ) : (
            <Plus className="h-8 w-8" />
          )}
        </Button>
        
        <p className="text-sm text-muted-foreground">
          {showSuccess ? (config.successMessage || 'Logged!') : buttonText}
        </p>

        {todayCount > 0 && (
          <p className="text-xs text-muted-foreground">
            Today: {todayCount} {todayCount === 1 ? 'entry' : 'entries'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
